import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Radio,
  Button,
  Space,
  Card,
  Typography,
  message,
  Spin,
  Input as AntdInput,
  Divider,
  Badge,
} from "antd";
import { useUpdate } from "@pankod/refine-core";
import { Features } from "interfaces/features";

const { Title, Text } = Typography;

interface EditFeatureModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  categoryData?: Features.CategoryItem;
}

export const EditFeatureModal: React.FC<EditFeatureModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  categoryData,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState<Features.FeatureVisibility[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { mutate: updateFeature } = useUpdate();

  useEffect(() => {
    if (visible && categoryData) {
      // Featureları alfabetik sırala
      const sortedFeatures = [...(categoryData.features || [])].sort((a, b) =>
        a.name.localeCompare(b.name, "tr", {
          sensitivity: "base",
        })
      );
      setFeatures(sortedFeatures);
      form.setFieldsValue({
        categoryName: categoryData.categoryName,
      });
    }
  }, [visible, categoryData, form]);

  const handleFeatureVisibilityChange = (
    featureId: string,
    isVisible: boolean
  ) => {
    setFeatures((prevFeatures) =>
      prevFeatures.map((feature) =>
        feature.id === featureId ? { ...feature, isVisible } : feature
      )
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const updatedCategory = {
        ...values,
        features: features,
      };

      updateFeature(
        {
          resource: "feature-management",
          id: categoryData?.categoryId || "",
          values: updatedCategory,
          successNotification: false,
        },
        {
          onSuccess: () => {
            message.success("Kategori özellikleri başarıyla güncellendi");
            onSuccess();
            onCancel();
          },
          onError: (error) => {
            message.error("Güncelleme sırasında bir hata oluştu");
            console.error("Update error:", error);
          },
        }
      );
    } catch (error) {
      console.error("Form validation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFeatures(categoryData?.features || []);
    setSearchTerm("");
    onCancel();
  };

  // Filtrelenmiş ve sıralanmış featurelar
  const filteredFeatures = features
    .filter(
      (feature) =>
        feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.code.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      a.name.localeCompare(b.name, "tr", {
        sensitivity: "base",
      })
    );

  // Aktif ve deaktif feature sayıları
  const activeCount = features.filter((feat) => feat.isVisible).length;
  const inactiveCount = features.filter((feat) => !feat.isVisible).length;

  return (
    <Modal
      title="Kategori Özelliklerini Düzenle"
      visible={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          İptal
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Kaydet
        </Button>,
      ]}
      width={900}
      destroyOnClose
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            categoryName: categoryData?.categoryName || "",
          }}
        >
          <Form.Item
            name="categoryName"
            label="Kategori Adı"
            rules={[
              { required: true, message: "Kategori adı alanı zorunludur" },
            ]}
          >
            <Input disabled />
          </Form.Item>

          <Divider />

          <div>
            <Title level={4}>Özellik Görünürlük Ayarları</Title>
            <Text type="secondary">
              Kategori içindeki özelliklerin aktiflik durumunu aşağıdan
              yönetebilirsiniz.
            </Text>

            {/* İstatistikler */}
            <div
              style={{
                marginTop: 16,
                marginBottom: 16,
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <Card size="small" style={{ flex: 1, minWidth: 120 }}>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: "#52c41a",
                    }}
                  >
                    {activeCount}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Aktif</div>
                </div>
              </Card>
              <Card size="small" style={{ flex: 1, minWidth: 120 }}>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: "#ff4d4f",
                    }}
                  >
                    {inactiveCount}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Deaktif</div>
                </div>
              </Card>
              <Card size="small" style={{ flex: 1, minWidth: 120 }}>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: "#1890ff",
                    }}
                  >
                    {features.length}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Toplam</div>
                </div>
              </Card>
            </div>

            {/* Arama Alanı */}
            <div style={{ marginBottom: 16 }}>
              <AntdInput.Search
                placeholder="Özellik adı veya kodu ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                style={{ width: "100%" }}
                size="large"
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: "12px",
                  color: "#666",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <span>
                  {filteredFeatures.length} / {features.length} özellik
                  gösteriliyor
                </span>
                {filteredFeatures.length > 0 && (
                  <Space size="small">
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        setFeatures((prevFeatures) =>
                          prevFeatures.map((feature) =>
                            filteredFeatures.some((ff) => ff.id === feature.id)
                              ? { ...feature, isVisible: true }
                              : feature
                          )
                        );
                      }}
                    >
                      Filtrelenenleri Aktif Yap
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => {
                        setFeatures((prevFeatures) =>
                          prevFeatures.map((feature) =>
                            filteredFeatures.some((ff) => ff.id === feature.id)
                              ? { ...feature, isVisible: false }
                              : feature
                          )
                        );
                      }}
                    >
                      Filtrelenenleri Deaktif Yap
                    </Button>
                  </Space>
                )}
              </div>
            </div>

            {/* Featurelar Listesi - Scroll ile */}
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "8px",
              }}
            >
              {features.length === 0 ? (
                <Card>
                  <Text type="secondary">
                    Bu kategori için özellik bulunmamaktadır.
                  </Text>
                </Card>
              ) : filteredFeatures.length === 0 ? (
                <Card>
                  <Text type="secondary">
                    "{searchTerm}" araması için sonuç bulunamadı.
                  </Text>
                </Card>
              ) : (
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="small"
                >
                  {filteredFeatures.map((feature) => (
                    <Card
                      key={feature.id}
                      size="small"
                      style={{
                        marginBottom: 4,
                        border: feature.isVisible
                          ? "1px solid #52c41a"
                          : "1px solid #ff4d4f",
                        backgroundColor: feature.isVisible
                          ? "#f6ffed"
                          : "#fff2f0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 4 }}>
                            <Text
                              strong
                              style={{
                                color: feature.isVisible
                                  ? "#52c41a"
                                  : "#ff4d4f",
                              }}
                            >
                              {feature.name}
                            </Text>
                          </div>
                        </div>
                        <Radio.Group
                          value={feature.isVisible}
                          onChange={(e) =>
                            handleFeatureVisibilityChange(
                              feature.id,
                              e.target.value
                            )
                          }
                          buttonStyle="solid"
                          size="small"
                        >
                          <Radio.Button value={true}>Aktif</Radio.Button>
                          <Radio.Button value={false}>Deaktif</Radio.Button>
                        </Radio.Group>
                      </div>
                    </Card>
                  ))}
                </Space>
              )}
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};
